// React-query wiring for the productionized /supplier data layer. These hooks read
// and mutate the SAME live operator endpoints the full app uses, so the
// free-tier journey works on real data. MockDataContext composes them into the
// flat view-model the pages consume.

import { useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import http from 'helpers/requests';

import { useFetchLoggedInOperator } from 'modules/Operator/hooks/useFetchLoggedInOperator';
import { useCreateOperator } from 'modules/Operator/hooks/useCreateOperator';
import { useUpdateOperator } from 'modules/Operator/hooks/useUpdateOperator';
import { useFetchOperatorInstallations } from 'modules/OperatorInstallations/hooks/useFetchOperatorInstallations';
import { useCreateOperatorInstallation } from 'modules/OperatorInstallations/hooks/useCreateOperatorInstallation';
import { useUpdateOperatorInstallation } from 'modules/OperatorInstallations/hooks/useUpdateOperatorInstallation';

export { useFetchLoggedInOperator, useCreateOperator, useUpdateOperator };
export { useCreateOperatorInstallation, useUpdateOperatorInstallation };

// A fixed page window for the free tier — quota caps this well under 50.
const LIST_PAGINATION = { page: 1, limit: 50 };
const EMPTY_FORM = {};

/**
 * Fetch the operator's installations (ids from the search list) and then the
 * FULL document for each (which includes the nested `emissionsFactors`). Both
 * installations and emission factors are derived from these full docs, so a
 * single cache (`['singleInstallation', id]`) backs the whole journey.
 */
export function useLiteInstallations() {
  const { data: list, isLoading: listLoading } = useFetchOperatorInstallations({
    form: EMPTY_FORM,
    pagination: LIST_PAGINATION,
  });

  const ids = useMemo(
    () =>
      (list?.installations || [])
        .map((i) => i?._id)
        .filter(Boolean)
        .map(String),
    [list],
  );

  const detailQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['singleInstallation', id],
      queryFn: async () => {
        const res = await http.get(`/installations/operator/${id}`);
        return res.data;
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const docs = useMemo(() => {
    const out = [];
    ids.forEach((id, idx) => {
      const doc = detailQueries[idx]?.data;
      if (doc) out.push(doc);
    });
    return out;
  }, [ids, detailQueries]);

  return {
    docs,
    isLoading: listLoading || detailQueries.some((q) => q.isLoading),
  };
}

/**
 * Incoming + outgoing connection requests. Incoming come from both the
 * declarant queue (`/requests`) and the operator queue (`/requests/operator`);
 * outgoing are the operator requests this org sent (`?status=sent`).
 */
export function useLiteRequests(enabled = true) {
  const incomingDeclarants = useQuery({
    queryKey: ['operatorInvites', 'lite-incoming-declarant'],
    queryFn: async () => (await http.get('/requests')).data,
    enabled,
    staleTime: 0,
  });
  const incomingOperators = useQuery({
    queryKey: ['operatorInvites', 'lite-incoming-operator'],
    queryFn: async () => (await http.get('/requests/operator')).data,
    enabled,
    staleTime: 0,
  });
  const outgoing = useQuery({
    queryKey: ['operatorInvites', 'lite-outgoing'],
    queryFn: async () => (await http.get('/requests/operator?status=sent')).data,
    enabled,
    staleTime: 0,
  });

  // Memoise on the raw query data (whose reference is stable between refetches)
  // so the derived arrays don't churn on every provider render.
  const incDecl = useMemo(
    () => unwrapRequestList(incomingDeclarants.data),
    [incomingDeclarants.data],
  );
  const incOp = useMemo(() => unwrapRequestList(incomingOperators.data), [incomingOperators.data]);
  const out = useMemo(() => unwrapRequestList(outgoing.data), [outgoing.data]);

  return {
    incomingDeclarants: incDecl,
    incomingOperators: incOp,
    outgoing: out,
    isLoading: incomingDeclarants.isLoading || incomingOperators.isLoading || outgoing.isLoading,
  };
}

// The requests endpoints return either a bare array or a wrapped `{ requests }`
// / `{ data }` — normalise to an array.
function unwrapRequestList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.requests || data.data || data.invites || [];
}

/** Accept / reject / send connection requests. */
export function useLiteRequestMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['operatorInvites'] });

  const accept = useMutation({
    // Operator-queued requests accept via the operator route; declarant-queued
    // via the declarant route.
    mutationFn: async ({ id, isDeclarant }) =>
      (await http.put(isDeclarant ? `/requests/accept/${id}` : `/requests/operator/accept/${id}`))
        .data,
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async ({ id, isDeclarant }) =>
      (await http.remove(isDeclarant ? `/requests/${id}` : `/requests/operator/${id}`)).data,
    onSuccess: invalidate,
  });

  const send = useMutation({
    mutationFn: async (payload) => (await http.post('/requests/operator', payload)).data,
    onSuccess: invalidate,
  });

  return { accept, reject, send };
}
